import { ResultActions } from './ResultActions';
import { ResultButton } from './ResultButton';
import { ResultComposable } from './ResultComposable';
import { ResultIcon } from './ResultIcon';
import { ResultIllustration } from './ResultIllustration';
import { ResultSubTitle } from './ResultSubTitle';
import { ResultTitle } from './ResultTitle';
import type { ConfigurableResultAction, ConfigurableResultProps } from './types';

/**
 * Configurable Result component. This is the main API for most users.
 * Internally uses the composable Result API.
 */
export const Result = ({
  size = 'medium',
  status = 'empty',
  icon,
  illustration,
  title,
  headingLevel,
  subTitle,
  actionButtons = [],
  sx = {},
  dataTest,
  placeholderContainer = false,
  hideIcon = false
}: ConfigurableResultProps) => {
  // Mutually exclusive: icon or illustration
  if (icon && illustration) {
    throw new Error('Result: Provide either icon or illustration, not both.');
  }

  let iconElement = null;
  if (!hideIcon) {
    if (icon) {
      iconElement = <ResultIcon>{icon}</ResultIcon>;
    } else if (!illustration) {
      // Render the default icon for the status using the composable API's DefaultStatusIcon
      iconElement = (
        <ResultIcon>
          <ResultComposable.DefaultStatusIcon />
        </ResultIcon>
      );
    }
  }

  return (
    <ResultComposable
      size={size}
      status={status}
      sx={sx}
      dataTest={dataTest}
      placeholderContainer={placeholderContainer}
    >
      {iconElement}
      {!hideIcon && illustration && <ResultIllustration>{illustration}</ResultIllustration>}
      {title && <ResultTitle headingLevel={headingLevel}>{title}</ResultTitle>}
      {subTitle && <ResultSubTitle>{subTitle}</ResultSubTitle>}
      {actionButtons.length > 0 && (
        <ResultActions>
          {actionButtons.map((action: ConfigurableResultAction, index: number) => (
            <ResultButton key={`${action.label}-${index}`} {...action}>
              {action.label}
            </ResultButton>
          ))}
        </ResultActions>
      )}
    </ResultComposable>
  );
};
